import * as THREE from "three";
import { INCH_IN_PIXELS } from "~/composables/grid/constants";

const INCH_TO_METER = 0.0254;
const MM_TO_METER = 0.001;

export const pixelsToMeters = (pixels: number): number => {
  const inches = pixels / INCH_IN_PIXELS;
  return inches * INCH_TO_METER;
};

export const convertPosition = (
  x: number,
  y: number,
  canvasWidth: number,
  canvasHeight: number,
): THREE.Vector3 => {
  const xMeters = pixelsToMeters(x);
  const yMeters = pixelsToMeters(y);
  const centerX = pixelsToMeters(canvasWidth / 2);
  const centerY = pixelsToMeters(canvasHeight / 2);

  return new THREE.Vector3(xMeters - centerX, -(yMeters - centerY), 0);
};

export const createRectGeometry = (
  width: number,
  height: number,
  depthMm: number,
  _bevelEnabled: boolean,
): THREE.BufferGeometry => {
  const w = pixelsToMeters(width);
  const h = pixelsToMeters(height);
  const d = depthMm * MM_TO_METER;

  const geometry = new THREE.BoxGeometry(w, h, d);
  geometry.translate(0, 0, d / 2);

  return geometry;
};


/**
 * Peak tile geometry - flat tile with a peaked fold in the center third.
 * Flat bottom 33%, slopes to peak in middle 33%, flat top 33%.
 * Peak height: 2.375 inches from wall.
 */
export const PEAK_HEIGHT_INCHES = 2.375;

export const createPeakGeometry = (
  width: number,
  height: number,
  depthMm: number,
  offset = false,
): THREE.BufferGeometry => {
  const w = pixelsToMeters(width);
  const h = pixelsToMeters(height);
  const baseDepth = depthMm * MM_TO_METER;
  const peakHeight = PEAK_HEIGHT_INCHES * INCH_TO_METER;

  const geometry = new THREE.BufferGeometry();
  const positions: number[] = [];
  const uvs: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  const hw = w / 2;
  const hh = h / 2;

  // The peak portion is always 4.25 inches regardless of tile height.
  // For Small/Large: centered. For Offset: near the top.
  const peakWidthMeters = 4.25 * INCH_TO_METER;
  const halfPeakWidth = peakWidthMeters / 2;

  const yBottom = -hh;
  const yTop = hh;

  // Determine peak center position based on shape type
  // Default: centered (SmallPeak and LargeCenterPeak). Upper/offset: near the top.
  // In the browser AR pipeline, positive Y within the mesh = top on screen.
  let peakCenterY = 0; // centered
  if (offset) {
    const offsetFromTop = 6.5 * INCH_TO_METER;
    peakCenterY = hh - offsetFromTop;
  }

  const yFoldBottom = peakCenterY - halfPeakWidth;
  const yFoldTop = peakCenterY + halfPeakWidth;
  const yCenter = peakCenterY;

  const flatZ = baseDepth;
  const peakZ = baseDepth + peakHeight;

  // UV Y coordinates based on actual fold positions relative to tile height
  // UV Y coordinates based on actual fold positions
  const uvBottom = 0;
  const uvFoldBottom = (yFoldBottom - yBottom) / h;
  const uvCenter = (yCenter - yBottom) / h;
  const uvFoldTop = (yFoldTop - yBottom) / h;
  const uvTop = 1;

  const slopeDy = yCenter - yFoldBottom;
  const slopeDz = peakZ - flatZ;
  const slopeLen = Math.sqrt(slopeDy * slopeDy + slopeDz * slopeDz);
  const lowerNy = -slopeDz / slopeLen;
  const lowerNz = slopeDy / slopeLen;
  const upperNy = slopeDz / slopeLen;
  const upperNz = slopeDy / slopeLen;

  let idx = 0;

  // Front Panel 1: Bottom flat
  positions.push(-hw, yBottom, flatZ, hw, yBottom, flatZ, hw, yFoldBottom, flatZ, -hw, yFoldBottom, flatZ);
  normals.push(0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1);
  uvs.push(0, uvBottom, 1, uvBottom, 1, uvFoldBottom, 0, uvFoldBottom);
  indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
  idx += 4;

  // Front Panel 2: Lower slope
  positions.push(-hw, yFoldBottom, flatZ, hw, yFoldBottom, flatZ, hw, yCenter, peakZ, -hw, yCenter, peakZ);
  normals.push(0, lowerNy, lowerNz, 0, lowerNy, lowerNz, 0, lowerNy, lowerNz, 0, lowerNy, lowerNz);
  uvs.push(0, uvFoldBottom, 1, uvFoldBottom, 1, uvCenter, 0, uvCenter);
  indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
  idx += 4;

  // Front Panel 3: Upper slope
  positions.push(-hw, yCenter, peakZ, hw, yCenter, peakZ, hw, yFoldTop, flatZ, -hw, yFoldTop, flatZ);
  normals.push(0, upperNy, upperNz, 0, upperNy, upperNz, 0, upperNy, upperNz, 0, upperNy, upperNz);
  uvs.push(0, uvCenter, 1, uvCenter, 1, uvFoldTop, 0, uvFoldTop);
  indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
  idx += 4;

  // Front Panel 4: Top flat
  positions.push(-hw, yFoldTop, flatZ, hw, yFoldTop, flatZ, hw, yTop, flatZ, -hw, yTop, flatZ);
  normals.push(0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1);
  uvs.push(0, uvFoldTop, 1, uvFoldTop, 1, uvTop, 0, uvTop);
  indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
  idx += 4;

  // Back face (flat color)
  positions.push(-hw, yBottom, 0, hw, yBottom, 0, hw, yTop, 0, -hw, yTop, 0);
  normals.push(0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1);
  uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
  indices.push(idx, idx + 2, idx + 1, idx, idx + 3, idx + 2);
  idx += 4;

  // Left side - bottom flat
  positions.push(-hw, yBottom, 0, -hw, yBottom, flatZ, -hw, yFoldBottom, flatZ, -hw, yFoldBottom, 0);
  normals.push(-1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0);
  uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
  indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
  idx += 4;

  // Left side - peak triangle
  positions.push(-hw, yFoldBottom, flatZ, -hw, yCenter, peakZ, -hw, yFoldTop, flatZ);
  normals.push(-1, 0, 0, -1, 0, 0, -1, 0, 0);
  uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
  indices.push(idx, idx + 1, idx + 2);
  idx += 3;

  // Left side - under peak
  positions.push(-hw, yFoldBottom, 0, -hw, yFoldBottom, flatZ, -hw, yFoldTop, flatZ, -hw, yFoldTop, 0);
  normals.push(-1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0);
  uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
  indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
  idx += 4;

  // Left side - top flat
  positions.push(-hw, yFoldTop, 0, -hw, yFoldTop, flatZ, -hw, yTop, flatZ, -hw, yTop, 0);
  normals.push(-1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0);
  uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
  indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
  idx += 4;

  // Right side - bottom flat
  positions.push(hw, yBottom, 0, hw, yFoldBottom, 0, hw, yFoldBottom, flatZ, hw, yBottom, flatZ);
  normals.push(1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0);
  uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
  indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
  idx += 4;

  // Right side - peak triangle
  positions.push(hw, yFoldBottom, flatZ, hw, yFoldTop, flatZ, hw, yCenter, peakZ);
  normals.push(1, 0, 0, 1, 0, 0, 1, 0, 0);
  uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
  indices.push(idx, idx + 1, idx + 2);
  idx += 3;

  // Right side - under peak
  positions.push(hw, yFoldBottom, 0, hw, yFoldTop, 0, hw, yFoldTop, flatZ, hw, yFoldBottom, flatZ);
  normals.push(1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0);
  uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
  indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
  idx += 4;

  // Right side - top flat
  positions.push(hw, yFoldTop, 0, hw, yTop, 0, hw, yTop, flatZ, hw, yFoldTop, flatZ);
  normals.push(1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0);
  uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
  indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
  idx += 4;

  // Bottom edge
  positions.push(-hw, yBottom, 0, hw, yBottom, 0, hw, yBottom, flatZ, -hw, yBottom, flatZ);
  normals.push(0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0);
  uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
  indices.push(idx, idx + 2, idx + 1, idx, idx + 3, idx + 2);
  idx += 4;

  // Top edge
  positions.push(-hw, yTop, 0, hw, yTop, 0, hw, yTop, flatZ, -hw, yTop, flatZ);
  normals.push(0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0);
  uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
  indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);

  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(indices);

  return geometry;
};

export const createHexGeometry = (
  radius: number,
  sides: number,
  depthMm: number,
  _bevelEnabled: boolean,
): THREE.BufferGeometry => {
  const r = pixelsToMeters(radius);
  const d = depthMm * MM_TO_METER;
  const backZ = 0;
  const frontZ = d;

  const geometry = new THREE.BufferGeometry();

  const vertices2D: Array<[number, number]> = [];
  for (let i = 0; i < sides; i++) {
    const angle = (i / sides) * Math.PI * 2;
    const x = Math.cos(angle) * r;
    const y = Math.sin(angle) * r;
    vertices2D.push([x, y]);
  }

  const minX = Math.min(...vertices2D.map((v) => v[0]));
  const maxX = Math.max(...vertices2D.map((v) => v[0]));
  const minY = Math.min(...vertices2D.map((v) => v[1]));
  const maxY = Math.max(...vertices2D.map((v) => v[1]));
  const uvWidth = maxX - minX || 1;
  const uvHeight = maxY - minY || 1;

  const getUV = (x: number, y: number): [number, number] => {
    const u = (x - minX) / uvWidth;
    const v = (y - minY) / uvHeight;
    return [u, v];
  };

  const positions: number[] = [];
  const uvs: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i < sides; i++) {
    const [x, y] = vertices2D[i];
    positions.push(x, y, frontZ);
    const [u, v] = getUV(x, y);
    uvs.push(u, v);
    normals.push(0, 0, 1);
  }

  for (let i = 1; i < sides - 1; i++) {
    indices.push(0, i, i + 1);
  }

  const backOffset = sides;
  for (let i = 0; i < sides; i++) {
    const [x, y] = vertices2D[i];
    positions.push(x, y, backZ);
    const [u, v] = getUV(x, y);
    uvs.push(u, v);
    normals.push(0, 0, -1);
  }

  for (let i = 1; i < sides - 1; i++) {
    indices.push(backOffset + 0, backOffset + i + 1, backOffset + i);
  }

  let sideOffset = 2 * sides;
  for (let i = 0; i < sides; i++) {
    const va = vertices2D[i];
    const vb = vertices2D[(i + 1) % sides];

    const dx = vb[0] - va[0];
    const dy = vb[1] - va[1];
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = dy / len;
    const ny = -dx / len;

    positions.push(va[0], va[1], frontZ);
    positions.push(va[0], va[1], backZ);
    positions.push(vb[0], vb[1], backZ);
    positions.push(vb[0], vb[1], frontZ);

    normals.push(nx, ny, 0, nx, ny, 0, nx, ny, 0, nx, ny, 0);
    uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);

    indices.push(sideOffset + 0, sideOffset + 1, sideOffset + 2);
    indices.push(sideOffset + 0, sideOffset + 2, sideOffset + 3);

    sideOffset += 4;
  }

  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(indices);

  return geometry;
};

export const createPolygonGeometry = (
  points: number[],
  depthMm: number,
  _bevelEnabled: boolean,
): THREE.BufferGeometry => {
  const d = depthMm * MM_TO_METER;
  const backZ = 0;
  const frontZ = d;

  const geometry = new THREE.BufferGeometry();

  const vertices2D: Array<[number, number]> = [];
  for (let i = 0; i < points.length; i += 2) {
    const x = pixelsToMeters(points[i]);
    const y = -pixelsToMeters(points[i + 1]);
    vertices2D.push([x, y]);
  }

  const numVertices = vertices2D.length;
  const minX = Math.min(...vertices2D.map((v) => v[0]));
  const maxX = Math.max(...vertices2D.map((v) => v[0]));
  const minY = Math.min(...vertices2D.map((v) => v[1]));
  const maxY = Math.max(...vertices2D.map((v) => v[1]));
  const uvWidth = maxX - minX || 1;
  const uvHeight = maxY - minY || 1;

  const getUV = (x: number, y: number): [number, number] => {
    const u = (x - minX) / uvWidth;
    const v = (y - minY) / uvHeight;
    return [u, v];
  };

  const positions: number[] = [];
  const uvs: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  for (const [x, y] of vertices2D) {
    positions.push(x, y, frontZ);
    uvs.push(...getUV(x, y));
    normals.push(0, 0, 1);
  }

  for (let i = 1; i < numVertices - 1; i++) {
    indices.push(0, i + 1, i);
  }

  const backOffset = numVertices;
  for (const [x, y] of vertices2D) {
    positions.push(x, y, backZ);
    uvs.push(...getUV(x, y));
    normals.push(0, 0, -1);
  }

  for (let i = 1; i < numVertices - 1; i++) {
    indices.push(backOffset + 0, backOffset + i, backOffset + i + 1);
  }

  let sideOffset = 2 * numVertices;
  for (let i = 0; i < numVertices; i++) {
    const [ax, ay] = vertices2D[i];
    const [bx, by] = vertices2D[(i + 1) % numVertices];

    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = dy / len;
    const ny = -dx / len;

    positions.push(ax, ay, frontZ);
    positions.push(ax, ay, backZ);
    positions.push(bx, by, backZ);
    positions.push(bx, by, frontZ);

    normals.push(nx, ny, 0, nx, ny, 0, nx, ny, 0, nx, ny, 0);
    uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);

    indices.push(sideOffset, sideOffset + 1, sideOffset + 2);
    indices.push(sideOffset, sideOffset + 2, sideOffset + 3);

    sideOffset += 4;
  }

  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(indices);

  return geometry;
};
